import React, { useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroup,
  InputGroupText,
  Container,
  Row,
  Col,
} from "reactstrap";

import {
  useForm,
  Controller,
  SubmitHandler,
  FieldValues,
} from "react-hook-form";
import PageLayout from "../PageLayout";
import { changePasswordApi } from "src/network/api";
import { toast } from "react-toastify";

interface FormData {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const ChangePasswordPage: React.FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onChange",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string>("");

  const handleTogglePassword = (type: "current" | "new" | "confirm") => {
    if (type === "current") {
      setShowCurrentPassword(!showCurrentPassword);
    } else if (type === "new") {
      setShowNewPassword(!showNewPassword);
    } else if (type === "confirm") {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (data.new_password !== data.confirm_password) {
      setError("New password and confirmation do not match");
      return;
    }
    try {
      const response = await changePasswordApi(data);
      if (response.status === 200 && response.data.status === 200) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.message ?? e.message);
    }
  };

  return (
    <PageLayout>
      <main>
        <section
          className="section section-shaped section-lg"
          style={{
            backgroundColor: "#f8f9fa",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg="6">
                <Card
                  className="shadow border-0"
                  style={{
                    borderRadius: "15px",
                    backgroundColor: "white",
                  }}
                >
                  <CardHeader
                    className="text-white text-center"
                    style={{
                      backgroundColor: "#5e72e4",
                      borderRadius: "15px 15px 0 0",
                      padding: "1.5rem",
                    }}
                  >
                    <p style={{ fontSize: "1.7rem", margin: "0.5rem 0 0" }}>
                      Change your password
                    </p>
                  </CardHeader>
                  <CardBody className="px-lg-5 py-lg-5">
                    {/* Change Password Form */}
                    <Form role="form" onSubmit={handleSubmit(onSubmit)}>
                      {error && (
                        <div
                          className="alert alert-danger text-center"
                          role="alert"
                          style={{ marginBottom: "1rem", fontWeight: "bold" }}
                        >
                          {error}
                        </div>
                      )}

                      {/* Current Password */}
                      <FormGroup className="mb-4">
                        <InputGroup className="input-group-alternative">
                          <InputGroupText
                            style={{
                              backgroundColor: "transparent",
                              borderColor: "transparent",
                            }}
                          >
                            <i className="ni ni-lock-circle-open text-primary" />
                          </InputGroupText>
                          <Controller
                            name="old_password"
                            control={control}
                            rules={{
                              required: "Old password is required",
                            }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Current Password"
                                type={showCurrentPassword ? "text" : "password"}
                              />
                            )}
                          />
                          <InputGroupText
                            style={{
                              cursor: "pointer",
                              color: "#808080",
                              backgroundColor: "transparent",
                              borderColor: "transparent",
                            }}
                            onClick={() => handleTogglePassword("current")}
                          >
                            <i
                              className={`fas ${
                                showCurrentPassword ? "fa-eye-slash" : "fa-eye"
                              }`}
                            />
                          </InputGroupText>
                        </InputGroup>
                        {errors.old_password && (
                          <div
                            className="error-text"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {String(errors.old_password?.message)}
                          </div>
                        )}
                      </FormGroup>

                      {/* New Password */}
                      <FormGroup className="mb-4">
                        <InputGroup className="input-group-alternative">
                          <InputGroupText
                            style={{
                              backgroundColor: "transparent",
                              borderColor: "transparent",
                            }}
                          >
                            <i className="ni ni-lock-circle-open text-primary" />
                          </InputGroupText>
                          <Controller
                            name="new_password"
                            control={control}
                            rules={{
                              required: "New password is required",
                              minLength: {
                                value: 6,
                                message:
                                  "Password must be at least 6 characters",
                              },
                            }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="New Password"
                                type={showNewPassword ? "text" : "password"}
                              />
                            )}
                          />
                          <InputGroupText
                            style={{
                              cursor: "pointer",
                              color: "#808080",
                              backgroundColor: "transparent",
                              borderColor: "transparent",
                            }}
                            onClick={() => handleTogglePassword("new")}
                          >
                            <i
                              className={`fas ${
                                showNewPassword ? "fa-eye-slash" : "fa-eye"
                              }`}
                            />
                          </InputGroupText>
                        </InputGroup>
                        {errors.new_password && (
                          <div
                            className="error-text"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {String(errors.new_password?.message)}
                          </div>
                        )}
                      </FormGroup>

                      {/* Confirm Password */}
                      <FormGroup className="mb-4">
                        <InputGroup className="input-group-alternative">
                          <InputGroupText
                            style={{
                              backgroundColor: "transparent",
                              borderColor: "transparent",
                            }}
                          >
                            <i className="ni ni-lock-circle-open text-primary" />
                          </InputGroupText>
                          <Controller
                            name="confirm_password"
                            control={control}
                            rules={{
                              required: "Please confirm your new password",
                            }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Confirm New Password"
                                type={showConfirmPassword ? "text" : "password"}
                              />
                            )}
                          />
                          <InputGroupText
                            style={{
                              cursor: "pointer",
                              color: "#808080",
                              backgroundColor: "transparent",
                              borderColor: "transparent",
                            }}
                            onClick={() => handleTogglePassword("confirm")}
                          >
                            <i
                              className={`fas ${
                                showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                              }`}
                            />
                          </InputGroupText>
                        </InputGroup>
                        {errors.confirm_password && (
                          <div
                            className="error-text"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {String(errors.confirm_password?.message)}
                          </div>
                        )}
                      </FormGroup>

                      {/* Submit Button */}
                      <div className="text-center">
                        <Button
                          className="btn-lg"
                          color="primary"
                          type="submit"
                          style={{
                            backgroundColor: "#5e72e4",
                            borderColor: "transparent",
                            borderRadius: "50px",
                            padding: "0.8rem 2rem",
                          }}
                        >
                          Change Password
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
    </PageLayout>
  );
};

export default ChangePasswordPage;
